import React, { useRef } from 'react';
import { useLang } from '../../contexts/LangContext';
import { MediaImage } from './MediaImage';

interface ImageUploadFieldProps {
  label: string;
  imageUrl?: string;
  uploading?: boolean;
  removing?: boolean;
  disabled?: boolean;
  onUpload: (file: File) => void;
  onRemove?: () => void;
  hint?: string;
  fit?: 'contain' | 'cover';
}

export const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  label,
  imageUrl,
  uploading = false,
  removing = false,
  disabled = false,
  onUpload,
  onRemove,
  hint,
  fit = 'cover',
}) => {
  const { t } = useLang();
  const inputRef = useRef<HTMLInputElement>(null);
  const imageFitClass = fit === 'contain' ? 'object-contain' : 'object-cover';

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        {imageUrl ? (
          <MediaImage
            src={imageUrl}
            alt="Preview"
            className={`w-full max-h-52 ${imageFitClass} rounded-lg mb-3 bg-white`}
            fallbackClassName="w-full h-40 rounded-lg mb-3"
          />
        ) : (
          <div className="w-full h-40 rounded-lg bg-white border border-dashed border-gray-300 flex items-center justify-center text-gray-400 mb-3">
            <div className="text-center">
              <svg className="w-10 h-10 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-sm">{t('imageUploadEmpty')}</span>
            </div>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          disabled={disabled || uploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUpload(file);
            e.target.value = '';
          }}
        />

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={disabled || uploading || removing}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            {uploading ? t('imageUploading') : imageUrl ? t('imageUploadReplace') : t('imageUploadChoose')}
          </button>

          {imageUrl && onRemove && (
            <button
              type="button"
              disabled={disabled || uploading || removing}
              onClick={onRemove}
              className="inline-flex items-center px-4 py-2 bg-white border border-red-200 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              {removing ? t('imageUploadRemoving') : t('imageUploadRemove')}
            </button>
          )}
        </div>

        {hint && <p className="text-xs text-gray-500 mt-2">{hint}</p>}
      </div>
    </div>
  );
};
